import React, { useEffect, useRef, useState } from 'react';
import { fetchWithCorpId } from '../utils/api';
import { CONTACT_OWNERS, listContacts, saveContacts } from '../utils/contacts';
import { useParams } from 'react-router-dom';
import { Section, Field, FormLoading } from './ui/formkit';
import { useToast } from './ui/Toast';
import ContactPanel from './ContactPanel';
import { toDateInputValue } from '../utils/dateUtils';

/**
 * Used in two contexts:
 *   1. Standalone page (route)
 *      → id from useParams, renders its own Save/Cancel buttons.
 *   2. Inside a Drawer (from VendorTable)
 *      → id from `vendorId` prop; pass `formRef` so the drawer footer
 *        can trigger submit; pass `hideActions` to skip the inline buttons.
 *
 * A plain single-section CRUD form (no stepper, no contacts).
 */
const DEFAULT_VENDOR = {
  name: '', status: '', startDate: '', endDate: '', accessCode: '',
  address: '', city: '', state: '', zip: '', invoiced: false,
};

function VendorForm({
  mode = 'create',
  vendorId,                 // optional id, overrides URL param
  formRef,                  // optional ref attached to <form>
  hideActions = false,      // hide inline Save/Cancel (drawer drives submit via formRef)
  onSuccess,
  onCancel,
}) {
  const params = useParams();
  const toast = useToast();
  const id = vendorId ?? params.id;
  const isEdit = mode === 'edit';
  const [vendor, setVendor] = useState(DEFAULT_VENDOR);
  const [message, setMessage] = useState('');
  const [loadingRecord, setLoadingRecord] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const initialSnapshotRef = useRef(isEdit ? null : JSON.stringify(DEFAULT_VENDOR));
  // Suppliers could not have contacts before backend #148; they can now.
  const [contacts, setContacts] = useState([]);
  const loadedContactsRef = useRef([]);

  useEffect(() => {
    const fetchVendor = async () => {
      if (isEdit && id) {
        setLoadingRecord(true);
        try {
          const data = await fetchWithCorpId(`/vendors/${id}`);
          const loadedContacts = await listContacts(CONTACT_OWNERS.VENDOR, id);
          loadedContactsRef.current = loadedContacts;
          setContacts(loadedContacts);
          const normalized = {
            ...data,
            startDate: toDateInputValue(data.startDate),
            endDate: toDateInputValue(data.endDate),
          };
          setVendor(normalized);
          initialSnapshotRef.current = JSON.stringify(normalized);
        } catch (error) {
          setMessage('Could not load this vendor: ' + error.message);
        } finally {
          setLoadingRecord(false);
        }
      } else {
        setLoadingRecord(false);
      }
    };
    fetchVendor();
  }, [isEdit, id]);

  const isDirty = initialSnapshotRef.current !== null
    && JSON.stringify(vendor) !== initialSnapshotRef.current;

  const handleChange = (e) => {
    const { name, type, value, checked } = e.target;
    const nextValue = type === 'checkbox' ? Boolean(checked) : value;
    setVendor((prev) => ({ ...prev, [name]: nextValue }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (saving) return;
    setMessage('');

    if (!(vendor.name || '').trim()) {
      setMessage('Fill in the required fields: Name.');
      return;
    }

    setSaving(true);
    try {
      const url = isEdit && vendor.id ? `/vendors/${vendor.id}` : '/vendors';
      const response = await fetchWithCorpId(url, {
        method: isEdit ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(vendor),
      });
      if (response) {
        const savedContacts = await saveContacts(
          CONTACT_OWNERS.VENDOR, response.id || vendor.id, contacts, loadedContactsRef.current);
        loadedContactsRef.current = savedContacts;
        setContacts(savedContacts);
        initialSnapshotRef.current = JSON.stringify(vendor);
        toast.success(isEdit ? 'Vendor updated' : 'Vendor created');
        onSuccess?.();
      } else {
        setMessage('Failed to save. Please try again.');
      }
    } catch (error) {
      setMessage(error.message || 'Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loadingRecord) {
    return <FormLoading label="Loading vendor…" />;
  }

  return (
    <form
      ref={formRef}
      onSubmit={handleSubmit}
      noValidate={!isEdit}
      data-dirty={isDirty ? 'true' : 'false'}
    >
      {message && (
        <div className="alert error" role="alert" style={{ marginBottom: '20px' }}>
          {message}
        </div>
      )}

      <Section title="Vendor" description="Name and operational status.">
        <Field label="Name" span={2} required>
          <input type="text" name="name" value={vendor.name || ''} onChange={handleChange} placeholder="e.g. Acme Supplies" required />
        </Field>
        <Field label="Status">
          <select name="status" value={vendor.status || ''} onChange={handleChange}>
            <option value="">Select status</option>
            <option value="Active">Active</option>
            <option value="Inactive">Inactive</option>
          </select>
        </Field>
        <Field label="Access code" hint="Optional code for supplier access.">
          <input type="text" name="accessCode" value={vendor.accessCode || ''} onChange={handleChange} placeholder="Optional" />
        </Field>
        <Field label="Start date">
          <input type="date" name="startDate" value={vendor.startDate || ''} onChange={handleChange} />
        </Field>
        <Field label="End date">
          <input type="date" name="endDate" value={vendor.endDate || ''} onChange={handleChange} />
        </Field>
      </Section>

      <Section title="Address">
        <Field label="Address" span={2}>
          <input type="text" name="address" value={vendor.address || ''} onChange={handleChange} placeholder="Street address" />
        </Field>
        <Field label="City">
          <input type="text" name="city" value={vendor.city || ''} onChange={handleChange} />
        </Field>
        <Field label="State">
          <input type="text" name="state" value={vendor.state || ''} onChange={handleChange} />
        </Field>
        <Field label="ZIP">
          <input type="text" name="zip" value={vendor.zip || ''} onChange={handleChange} />
        </Field>
      </Section>

      <Section title="Billing" grid={null}>
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
          <input type="checkbox" name="invoiced" checked={Boolean(vendor.invoiced)} onChange={handleChange} />
          <span style={{
            fontFamily: 'var(--font-sans)', fontSize: '14px', color: 'var(--ink)',
          }}>
            Invoiced
          </span>
        </label>
      </Section>

      <Section title="Contacts" description="People to reach at this supplier." grid={null}>
        <ContactPanel contacts={contacts} setContacts={setContacts} />
      </Section>

      {!hideActions && (
        <div style={{ marginTop: '24px', display: 'flex', gap: '12px' }}>
          <button type="submit" className="btn-primary" disabled={saving}>
            {saving ? 'Saving…' : (isEdit ? 'Save' : 'Create vendor')}
          </button>
          {onCancel && (
            <button type="button" onClick={onCancel} className="btn-secondary" disabled={saving}>Cancel</button>
          )}
        </div>
      )}
    </form>
  );
}

export default VendorForm;
